import React, { useState, useEffect } from "react";
import { Edit, Eye, Trash2, Pen } from "lucide-react";
import { DataTable, Badge, Button, InlineLoader, Pagination } from "../../../components/ui";
import { fmtDate } from "../../../lib/formatters";

export const InventoryTable = ({
  activeTab,
  data = [],
  onView,
  onEdit,
  onDelete,
  isLoading
}) => {
  const [page, setPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    setPage(1);
  }, [data, activeTab]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20 bg-white rounded-xl border border-slate-100 shadow-sm">
        <InlineLoader />
      </div>
    );
  }

  const columns = (() => {
    const commonActions = {
      header: "ACTIONS",
      className: "text-center w-28",
      render: (item) => (
        <div className="flex items-center justify-center gap-1">
          <Button variant="ghost" size="xs" onClick={() => onView(item)} title="View Details">
            <Eye size={14} className="text-slate-500" />
          </Button>
          <Button variant="ghost" size="xs" onClick={() => onEdit(item)} title="Edit Item">
            <Edit size={14} className="text-brand-blue" />
          </Button>
          <Button variant="ghost" size="xs" onClick={() => onDelete(item._id)} title="Delete Item">
            <Trash2 size={14} className="text-red-500" />
          </Button>
        </div>
      )
    };

    const statusCol = {
      header: "Status",
      render: (item) => {
        const isActive = item.status ? item.status === 'ACTIVE' : item.active !== false;
        return isActive ? (
          <Badge variant="success" label="Active" dot />
        ) : (
          <Badge variant="danger" label="Inactive" dot />
        );
      }
    };

    const safeString = (val) => {
      if (val === undefined || val === null || val === "") return "—";
      if (typeof val === 'string') return val;
      if (typeof val === 'number') return String(val);
      if (typeof val === 'object') {
        const candidate = val.subCategoryName || val.categoryName || val.brandName || val.unitName || val.storeName || val.registeredTypeName || val.name || val.title || val.label;
        if (typeof candidate === 'string') return candidate;
        if (typeof candidate === 'number') return String(candidate);
        return "—";
      }
      return String(val);
    };

    const dateCol = {
      header: "CREATED AT",
      render: (item) => (
        <span className="text-[12px] text-slate-500 font-medium">
          {fmtDate(item.createdAt)}
        </span>
      )
    };

    const idStyle = "text-[11px] font-bold text-slate-700 tracking-wider uppercase";
    const nameStyle = "font-semibold text-slate-800 text-[12px] truncate block max-w-[200px]";
    const textStyle = "text-[12px] font-medium text-slate-600";
    const truncateStyle = "text-[12px] font-medium text-slate-600 truncate block max-w-[200px]";

    const renderId = (val) => <span className={idStyle}>{safeString(val)}</span>;
    const renderName = (val) => <span className={nameStyle} title={safeString(val)}>{safeString(val)}</span>;
    const renderText = (val) => <span className={textStyle}>{safeString(val)}</span>;
    const renderTruncate = (val) => <span className={truncateStyle} title={safeString(val)}>{safeString(val)}</span>;

    const codeCol = (key = "code", label = "CODE") => ({
      header: label,
      render: (item) => renderId(item[key])
    });

    const editActionCol = {
      header: "Actions",
      className: "text-center w-20",
      render: (item) => (
        <div className="flex items-center justify-center gap-1">
          <Button variant="ghost" size="xs" onClick={() => onEdit(item)} title="Edit Item">
            <Pen size={14} className="text-brand-blue" />
          </Button>
        </div>
      )
    };

    const viewEditActionCol = {
      header: "Actions",
      className: "text-center w-24",
      render: (item) => (
        <div className="flex items-center justify-center gap-1">
          <Button variant="ghost" size="xs" onClick={() => onView(item)} title="View Details">
            <Eye size={14} className="text-slate-500" />
          </Button>
          <Button variant="ghost" size="xs" onClick={() => onEdit(item)} title="Edit Item">
            <Pen size={14} className="text-brand-blue" />
          </Button>
        </div>
      )
    };

    switch (activeTab) {
      case "vendor":
        return [
          { header: "Vendor ID", render: (item) => renderId(item.vendorId) },
          codeCol("vendorCode", "Vendor Code"),
          { header: "Vendor Name", render: (item) => renderName(item.vendorName) },
          { header: "Mobile", render: (item) => renderText(item.mobile) },
          {
            header: "Address",
            render: (item) => (
              <span className={truncateStyle} title={safeString(item.addressLine1)}>
                {safeString(item.addressLine1).length > 100 ? `${safeString(item.addressLine1).substring(0, 100)}...` : safeString(item.addressLine1)}
              </span>
            )
          },
          { header: "City", render: (item) => renderText(item.city) },
          { header: "State", render: (item) => renderText(item.state) },
          { header: "Pincode", render: (item) => renderText(item.pincode) },
          statusCol,
          viewEditActionCol
        ];
      case "brand":
        return [
          { header: "Brand ID", render: (item) => renderId(item.brandId) },
          codeCol("brandCode", "Brand Code"),
          { header: "Brand Name", render: (item) => renderName(item.brandName) },
          { header: "Description", render: (item) => renderTruncate(item.description) },
          statusCol,
          editActionCol
        ];
      case "item":
        return [
          { header: "Item ID", render: (item) => renderId(item.itemId) },
          codeCol("itemCode", "Item Code"),
          { header: "Item Name", render: (item) => renderName(item.itemName) },
          { header: "Type", render: (item) => <span className="text-[11px] text-slate-500 bg-slate-50 px-2 py-0.5 rounded border border-slate-100 uppercase">{safeString(item.itemType) || "Product"}</span> },
          { header: "Category", render: (item) => renderText(item.category) },
          { header: "Brand", render: (item) => renderText(item.brand) },
          { header: "Units", render: (item) => renderText(item.units) },
          statusCol,
          viewEditActionCol
        ];
      case "category":
        return [
          { header: "Category ID", render: (item) => renderId(item.categoryId) },
          codeCol("categoryCode", "Category Code"),
          { header: "Category Name", render: (item) => renderName(item.categoryName) },
          { header: "Description", render: (item) => renderTruncate(item.description) },
          statusCol,
          editActionCol
        ];
      case "subCategory":
        return [
          { header: "Sub Category ID", render: (item) => renderId(item.subCategoryId) },
          codeCol("subCategoryCode", "Sub Category Code"),
          { header: "Sub Category Name", render: (item) => renderName(item.subCategoryName) },
          { header: "Description", render: (item) => renderTruncate(item.description) },
          statusCol,
          editActionCol
        ];
      case "stores":
        return [
          { header: "Store ID", render: (item) => renderId(item.storeId || "—") },
          codeCol("storeCode", "Store Code"),
          { header: "Store Name", render: (item) => renderName(item.storeName) },
          {
            header: "Address",
            render: (item) => (
              <span className={truncateStyle} title={safeString(item.addressLine1)}>
                {safeString(item.addressLine1)} {item.city ? `, ${safeString(item.city)}` : ""}
              </span>
            )
          },
          { header: "Contact", render: (item) => renderText(item.mobile || "—") },
          { header: "Description", render: (item) => renderTruncate(item.description || "—") },
          statusCol,
          viewEditActionCol
        ];
      case "unit":
        return [
          { header: "Unit ID", render: (item) => renderId(item.unitId) },
          codeCol("unitCode", "Unit Code"),
          { header: "Unit Name", render: (item) => renderName(item.unitName) },
          { header: "Description", render: (item) => renderTruncate(item.description) },
          statusCol,
          editActionCol
        ];
      case "registeredType":
        return [
          { header: "Type ID", render: (item) => renderId(item.registeredTypeId) },
          codeCol("registeredTypeCode", "Type Code"),
          { header: "Type Name", render: (item) => renderName(item.registeredTypeName) },
          { header: "Description", render: (item) => renderTruncate(item.description) },
          statusCol,
          editActionCol
        ];
      default:
        return [
          codeCol(),
          { header: "TITLE", render: (item) => renderName(item.title) },
          { header: "DESCRIPTION", render: (item) => renderText(item.description) },
          dateCol,
          statusCol,
          commonActions
        ];
    }
  })();

  const currentData = (data || []).slice((page - 1) * itemsPerPage, page * itemsPerPage);
  const totalPages = Math.max(Math.ceil((data || []).length / itemsPerPage), 1);

  return (
    <div className="card overflow-hidden">
      <DataTable
        columns={columns}
        data={currentData}
        tableClassName="bg-white"
        getRowClassName={(idx) => idx % 2 === 0 ? "bg-white" : "bg-slate-50/10"}
      />

      {(data || []).length > itemsPerPage && (
        <Pagination
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
        />
      )}
    </div>
  );
};

